import { useAnalytics, ExportFormat, CreateViewStateFn } from '@ecosystem/newron-client-lib';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useExportModal } from '@hooks/export/export-modal/UseExportModal';
import { EmptyTabsPage, EnabledExportPages, ViewType } from '@hooks/export/Model';
import { SaleTypeMapping } from '@const/PerformanceType';
import { ColumnHeaderForOR, ColumnHeaderDisplayNameForOR, SaleType } from '@models/OpportunityReview';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  dataExportInitiatedTrackingFunction,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import {
  pageElementClickTrackingFunction,
  PageElementClickTrackingParam,
  PAGE_ELEMENT_CLICK_TRACKING_TITLE,
  TOGGLE_CLICKED_TITLE,
} from '@utils/page-analytics/model/PageElementClickedTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getPageTypeWithPageName } from '@utils/pageConfig/Types';
import { OPPORTUNITY_REVIEW_CARD_TITLE } from '../Utilities';
import { OpportunityReviewBubbleChartInsight } from '@components/collections/opportunity-review-bubble-chart/Model';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import OpportunityReviewCollectionButton from '../../components/opportunity-review-collection-button/OpportunityReviewCollectionButton';

import * as styles from './OpportunityReviewHeaderIcon.module.scss';
import { WithExportModal } from '@components/design';
import { ToggleViewButtons } from '@components/toggle-view-buttons/ToggleViewButton';

interface Props {
  displayTable: boolean;
  onChartDownload: (exportFormats: ExportFormat[]) => Promise<void>;
  isExportButtonDisabled: boolean;
  saleType: SaleType;
  setIsExportModalOpen: (isExportModalOpen: boolean) => void;
  volume: string;
  createInsightDataFn: CreateInsightDataFn<OpportunityReviewBubbleChartInsight>;
  onClick: (title: ViewType) => void;
  createViewStateFn?: CreateViewStateFn<any>;
}

const OpportunityReviewHeaderIcon: React.FC<Props> = ({
  displayTable,
  onChartDownload,
  isExportButtonDisabled,
  saleType,
  setIsExportModalOpen,
  volume,
  onClick,
  createInsightDataFn,
  createViewStateFn,
}) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);

  // Icon Click Tracking Function
  const handleElementClickTracking = (title: string) => {
    const params: PageElementClickTrackingParam = {
      eventTitle: PAGE_ELEMENT_CLICK_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      chartName: OPPORTUNITY_REVIEW_CARD_TITLE,
      optionChosen: title,
      elementName: TOGGLE_CLICKED_TITLE,
      access: pagesConfig.access,
    };
    pageElementClickTrackingFunction(params);
  };

  const onChange = (title: ViewType) => {
    handleElementClickTracking(title);
    onClick(title);
  };

  const viewType = displayTable ? ViewType.Table : ViewType.BubbleChart;
  const {
    openExportModal,
    onCloseExportModal,
    onExportError,
    exportModalFormats,
    exportModalMessage,
    exportModalHeaderText,
    isExportModalOpen,
  } = useExportModal({
    currentPage: EnabledExportPages.OpportunityReview,
    currentTab: EmptyTabsPage.None,
    currentView: viewType,
  });

  const handleDataExportTrackingFunction = useDataExportTracking('mainFilters');
  const handleExportTracking = () => {
    const elementKpi = displayTable
      ? ColumnHeaderDisplayNameForOR(volume)[pagesConfig.sortingOptionsOpportunityReview.headerKey as ColumnHeaderForOR]
      : SaleTypeMapping(volume)[saleType];
    handleDataExportTrackingFunction(
      {
        elementType: displayTable ? ElementType.TABLE : ElementType.BUBBLE_CHART,
        elementKpi: elementKpi,
        elementSort: pagesConfig.sortingOptionsOpportunityReview,
      },
      dataExportInitiatedTrackingFunction,
    );
  };

  const onExportClick = () => {
    handleExportTracking();
    openExportModal();
  };

  useEffect(() => {
    setIsExportModalOpen(isExportModalOpen);
  }, [isExportModalOpen]);

  return (
    <span className={styles.OpportunityReviewHeaderIcon__Row}>
      <OpportunityReviewCollectionButton
        isTableView={displayTable}
        isDisabled={isExportButtonDisabled}
        createInsightDataFn={createInsightDataFn}
        createViewStateFn={createViewStateFn}
      />
      <ToggleViewButtons {...{ view: viewType, isExportDisabled: isExportButtonDisabled, onExportClick, onChange }} />
      <WithExportModal
        title={exportModalHeaderText}
        isShown={isExportModalOpen}
        exportFormats={exportModalFormats}
        message={exportModalMessage}
        download={onChartDownload}
        onClose={onCloseExportModal}
        onError={onExportError}
      />
    </span>
  );
};

export default OpportunityReviewHeaderIcon;
